/**
 * Language Context
 * Provides language management functionality throughout the app
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nLanguageCode, ApiLanguageCode, mapI18nLangToApi } from '../i18n/languageMapper';
import languageService from '../services/languageService';
import { SUPPORTED_LANGUAGES } from '../i18n/i18n.config';

/**
 * Language Context Type
 */
interface LanguageContextType {
  currentLanguage: I18nLanguageCode;
  isLoading: boolean;
  changeLanguage: (langCode: I18nLanguageCode) => Promise<void>;
  supportedLanguages: typeof SUPPORTED_LANGUAGES;
  t: (key: string, options?: any) => string;
  updateLanguageOnAPI?: (apiUrl: string, authToken?: string) => Promise<boolean>;
}

/**
 * Language Context
 */
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * Language Provider Props
 */
interface LanguageProviderProps {
  children: ReactNode;
  apiUrl?: string; // Optional API endpoint to fetch/update language
  authToken?: string; // Optional auth token for API requests
  onLanguageChange?: (language: I18nLanguageCode) => void; // Callback when language changes
}

/**
 * Language Provider Component
 * Wraps the app and provides language management
 */
export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  apiUrl,
  authToken,
  onLanguageChange,
}) => {
  const { t, i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<I18nLanguageCode>(
    i18n.language as I18nLanguageCode
  );
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Initialize language on mount
   */
  useEffect(() => {
    const initLanguage = async () => {
      setIsLoading(true);
      try {
        const initialLang = await languageService.initializeLanguage(apiUrl, authToken);
        setCurrentLanguage(initialLang);
        onLanguageChange?.(initialLang);
      } catch (error) {
        console.error('[LanguageContext] Error initializing language:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initLanguage();
  }, [apiUrl, authToken]);

  /**
   * Listen for language changes from i18n
   */
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      const newLang = lng as I18nLanguageCode;
      setCurrentLanguage(newLang);
      onLanguageChange?.(newLang);
    };

    i18n.on('languageChanged', handleLanguageChange);

    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n, onLanguageChange]);

  /**
   * Change language handler
   */
  const handleChangeLanguage = async (langCode: I18nLanguageCode) => {
    try {
      await languageService.changeLanguage(langCode, true);
      setCurrentLanguage(langCode);

      // Optionally update on API if URL is provided
      if (apiUrl) {
        const apiLangCode = mapI18nLangToApi(langCode);
        await languageService.updateLanguageOnAPI(apiUrl, apiLangCode, authToken);
      }

      console.log('[LanguageContext] Language changed to:', langCode);
    } catch (error) {
      console.error('[LanguageContext] Error changing language:', error);
    }
  };

  /**
   * Update language on API
   */
  const updateLanguageOnAPI = async (url: string, token?: string): Promise<boolean> => {
    try {
      const apiLangCode = mapI18nLangToApi(currentLanguage);
      return await languageService.updateLanguageOnAPI(url, apiLangCode, token);
    } catch (error) {
      console.error('[LanguageContext] Error updating language on API:', error);
      return false;
    }
  };

  const contextValue: LanguageContextType = {
    currentLanguage,
    isLoading,
    changeLanguage: handleChangeLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    t,
    updateLanguageOnAPI,
  };

  // CRITICAL: Don't render children until language is initialized
  // This prevents UI from showing in wrong language
  if (isLoading) {
    return (
      <LanguageContext.Provider value={contextValue}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#0B0F14',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e94560',
            borderTop: '4px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }} />
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </LanguageContext.Provider>
    );
  }

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

/**
 * Custom hook to use Language Context
 * @throws Error if used outside LanguageProvider
 */
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);

  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }

  return context;
};

/**
 * Higher-order component to inject language context
 */
export const withLanguage = <P extends object>(
  Component: React.ComponentType<P & LanguageContextType>
) => {
  return (props: P) => {
    const languageContext = useLanguage();
    return <Component {...props} {...languageContext} />;
  };
};

export default LanguageContext;